import { Children, cloneElement, isValidElement, useId } from "react";
import { getWarningTone, titleCaseComplianceLabel } from "../../constants/workspaceCompliance";
import InfoTooltip from "../common/InfoTooltip";

export const fieldClass =
  "h-11 w-full min-w-0 rounded-xl border border-slate-300 px-4 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10";

export const textareaClass =
  "min-h-[120px] w-full min-w-0 rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10";

export function SectionCard({ title, description, children, tooltip = "" }) {
  return (
    <section className="scroll-mt-28 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-4 py-4 sm:px-6">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h2 className="break-words text-xl font-semibold text-slate-900">{title}</h2>
          {tooltip ? (
            <InfoTooltip
              text={tooltip}
              label={title}
              ariaLabel="More information"
              placement="bottom"
              maxWidth={300}
            />
          ) : null}
        </div>
        {description ? (
          <p className="mt-1 break-words text-sm leading-6 text-slate-500">{description}</p>
        ) : null}
      </div>
      <div className="min-w-0 space-y-5 px-4 py-6 sm:px-6">{children}</div>
    </section>
  );
}

export function InputLabel({ label, children, hint = "", tooltip = "" }) {
  const generatedId = useId();
  const child =
    Children.count(children) === 1 && isValidElement(children)
      ? Children.only(children)
      : null;
  const controlId = child ? child.props.id || `workspace-field-${generatedId}` : undefined;
  const renderedChildren = child ? cloneElement(child, { id: controlId }) : children;

  return (
    <div className="min-w-0 space-y-2">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <label
          htmlFor={controlId}
          className="break-words text-sm font-semibold uppercase tracking-wide text-slate-500"
        >
          {label}
        </label>
        {tooltip ? (
          <InfoTooltip
            text={tooltip}
            label={label}
            ariaLabel="More information"
            placement="bottom"
            maxWidth={300}
          />
        ) : null}
      </div>
      {renderedChildren}
      {hint ? <p className="break-words text-xs leading-5 text-slate-500">{hint}</p> : null}
    </div>
  );
}

export function BooleanCard({
  label,
  description,
  checked,
  onChange,
  trueLabel = "Yes",
  falseLabel = "No",
  tooltip = "",
}) {
  return (
    <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <div className="break-words text-sm font-semibold text-slate-900">{label}</div>
            {tooltip ? (
              <InfoTooltip
                text={tooltip}
                label={label}
                ariaLabel="More information"
                placement="bottom"
                maxWidth={300}
              />
            ) : null}
          </div>
          {description ? <p className="mt-1 break-words text-sm leading-6 text-slate-600">{description}</p> : null}
        </div>
        <div className="flex max-w-full shrink-0 flex-wrap rounded-full border border-slate-300 bg-white p-1 text-sm">
          <button
            type="button"
            className={`rounded-full px-4 py-2 text-center font-semibold ${
              checked ? "bg-slate-900 text-white" : "text-slate-600"
            }`}
            onClick={() => onChange(true)}
          >
            {trueLabel}
          </button>
          <button
            type="button"
            className={`rounded-full px-4 py-2 text-center font-semibold ${
              !checked ? "bg-slate-900 text-white" : "text-slate-600"
            }`}
            onClick={() => onChange(false)}
          >
            {falseLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function WarningBanner({
  strictModeEnabled = false,
  publishBlocked = false,
  agreementCompletionBlocked = false,
  warningStateLevel = "none",
  warnings = [],
  disclaimer = false,
}) {
  if (
    !strictModeEnabled &&
    !publishBlocked &&
    !agreementCompletionBlocked &&
    (!Array.isArray(warnings) || warnings.length === 0) &&
    warningStateLevel === "none" &&
    !disclaimer
  ) {
    return null;
  }

  return (
    <section className="scroll-mt-28 space-y-3 overflow-hidden rounded-3xl border border-slate-200 bg-white px-4 py-5 shadow-sm sm:px-6">
      {(publishBlocked || agreementCompletionBlocked) ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-800">
          <div className="font-semibold">Action required before moving forward</div>
          <p className="mt-2 leading-6">
            This workspace has setup items to review before it is published or before a renter
            completes the agreement. Review the notes below, update the setup if needed, then mark
            the current version reviewed.
          </p>
        </div>
      ) : null}
        <div className="flex min-w-0 flex-wrap gap-3">
        {strictModeEnabled ? (
          <span className="break-words rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-700">
            Review required
          </span>
        ) : null}
        {publishBlocked ? (
          <span className="break-words rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-rose-700">
            Listing blocked
          </span>
        ) : null}
        {agreementCompletionBlocked ? (
          <span className="break-words rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
            Agreement blocked
          </span>
        ) : null}
        {warningStateLevel && warningStateLevel !== "none" ? (
          <span className="break-words rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-700">
            Check level {titleCaseComplianceLabel(warningStateLevel)}
          </span>
        ) : null}
      </div>
      {Array.isArray(warnings) && warnings.length > 0 ? (
        <div className="grid gap-3">
          {warnings.map((warning, index) => (
            <article
              key={`${warning.warning_type || "warning"}-${index}`}
              className={`rounded-2xl border px-4 py-4 ${getWarningTone(warning.level)}`}
            >
              <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 break-words text-sm font-semibold">
                  {warning.message || titleCaseComplianceLabel(warning.warning_type)}
                </div>
                <span className="break-words rounded-full border border-current/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em]">
                  {titleCaseComplianceLabel(warning.level, "Info")}
                </span>
              </div>
              {warning.warning_type ? (
                <p className="mt-2 break-words text-xs font-semibold uppercase tracking-[0.16em]">
                  Item: {titleCaseComplianceLabel(warning.warning_type)}
                </p>
              ) : null}
              {Array.isArray(warning.factors) && warning.factors.length > 0 ? (
                <p className="mt-2 break-words text-sm leading-6">
                  Related setup: {warning.factors.map((item) => titleCaseComplianceLabel(item)).join(", ")}
                </p>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
      {disclaimer ? (
        <div className="break-words rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
          These checks help you catch missing or conflicting rental setup before a renter reviews
          the listing or agreement. They are operational guidance for this workspace and are not
          legal advice.
        </div>
      ) : null}
    </section>
  );
}
